import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  UserPlusIcon,
  UserMinusIcon,
  CheckBadgeIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { formatShortTime } from '../../utils/dateUtils';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { 
  fetchUserFollowers, 
  fetchUserFollowing, 
  fetchUserCollaborators,
  toggleFollow,
  clearAll,
  setInitialCounts
} from '../../store/slices/userRelationshipSlice';
import { UserRelationship } from '../../types';
import Button from '../common/Button';
import Avatar from '../common/Avatar';

interface UserRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  initialTab: 'followers' | 'following' | 'collaborators';
  userName: string;
  initialCounts?: {
    followers: number;
    following: number;
    collaborators: number;
  };
}

const UserRelationshipModal: React.FC<UserRelationshipModalProps> = ({
  isOpen,
  onClose,
  userId,
  initialTab,
  userName,
  initialCounts = { followers: 0, following: 0, collaborators: 0 },
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const { followers, following, collaborators, loading, pagination } = useAppSelector(
    (state) => state.userRelationship
  );
  const { user: currentUser } = useAppSelector((state) => state.auth);
  
  const [activeTab, setActiveTab] = useState<'followers' | 'following' | 'collaborators'>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [followingUsers, setFollowingUsers] = useState<Set<string>>(new Set());
  const [localFollowStates, setLocalFollowStates] = useState<Record<string, boolean>>({});

  const loadData = useCallback((tab: string) => {
    const params = {
      userId,
      page: 1,
      limit: 20,
      search: searchQuery || undefined,
    };

    switch (tab) {
      case 'followers':
        dispatch(fetchUserFollowers(params));
        break;
      case 'following':
        dispatch(fetchUserFollowing(params));
        break;
      case 'collaborators':
        dispatch(fetchUserCollaborators(params));
        break;
    }
  }, [userId, searchQuery, dispatch]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      dispatch(setInitialCounts(initialCounts));
    } else {
      dispatch(clearAll());
      setSearchQuery('');
    }
  }, [isOpen, initialTab, userId, dispatch, initialCounts]);

  useEffect(() => {
    if (isOpen) {
      const delay = searchQuery ? 500 : 0;
      const timeoutId = setTimeout(() => {
        loadData(activeTab);
      }, delay);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, isOpen, activeTab, loadData]);



  const handleTabChange = (tab: 'followers' | 'following' | 'collaborators') => {
    setActiveTab(tab);
    setSearchQuery('');
    setLocalFollowStates({});
    loadData(tab);
  };

  const handleFollowToggle = async (targetUserId: string) => {
    if (targetUserId === currentUser?.id) return;
    
    setFollowingUsers(prev => new Set(Array.from(prev).concat(targetUserId)));
    
    try {
      const response = await dispatch(toggleFollow(targetUserId)).unwrap();
      // Fix: Access nested data property if needed, though unwrap() might return the payload directly
      // Based on slice, it returns response.data which is { success, message, data: { following: boolean } }
      const isNowFollowing = response.data ? response.data.following : response.following;

      // Update local state immediately
      setLocalFollowStates(prev => ({
        ...prev,
        [targetUserId]: isNowFollowing
      }));

    } catch (error) {
      console.error('Failed to toggle follow:', error);
    } finally {
      setFollowingUsers(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(targetUserId);
        return newSet;
      });
    }
  };
  
  const handleMessage = (user: UserRelationship) => {
    onClose();
    navigate('/messages', {
      state: {
        openConversationWith: user.id,
        userName: `${user.first_name} ${user.last_name}`,
        userAvatar: user.profile_pic,
        userType: user.user_type,
      }
    });
  };

  const getFollowStatus = (user: UserRelationship) => {
    // Check local state first (user just clicked follow/unfollow)
    if (localFollowStates[user.id] !== undefined) {
      return localFollowStates[user.id];
    }

    // Determine default status based on tab
    if (activeTab === 'following') {
      return true;
    } else if (activeTab === 'followers') {
      return user.is_following_back;
    } else if (activeTab === 'collaborators') {
      return user.is_following;
    }

    // Fallback
    return user.is_following;
  };

  const renderUserCard = (user: UserRelationship) => {
    const followStatus = getFollowStatus(user);
    
    return (
      <motion.div
        key={user.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="group flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 border border-transparent hover:border-gray-100 dark:hover:border-gray-700 transition-all duration-200"
      >
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <Link to={`/profile/${user.id}`} onClick={onClose} className="block relative">
            <Avatar
              src={user.profile_pic_signed || user.profile_pic}
              alt={`${user.first_name} ${user.last_name}`}
              size="md"
              className="w-14 h-14 ring-2 ring-white dark:ring-gray-900 shadow-sm group-hover:shadow-md transition-all"
            />
            {/* Status Indicators */}
            <div className="absolute -bottom-1 -right-1 flex gap-0.5">
              {false && user.is_verified && (
                <div className="bg-white dark:bg-gray-900 rounded-full p-0.5 shadow-sm">
                  <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                </div>
              )}
              {user.user_type !== 'hirer' && user.is_available && (
                <div className="bg-white dark:bg-gray-900 rounded-full p-0.5 shadow-sm">
                  <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900"></div>
                </div>
              )}
            </div>
          </Link>
        </div>





          {/* User Info */}
          <div className="flex-1 min-w-0 py-1 relative">
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2 min-w-0">
                <Link 
                  to={`/profile/${user.id}`} 
                  onClick={onClose}
                  className="font-bold text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 truncate text-base transition-colors"
                >
                  {user.first_name} {user.last_name}
                </Link>
                
                {/* Special Badges */}
                {user.is_collaborator && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800 uppercase tracking-wider shrink-0">
                    Collab
                  </span>
                )}
                
                {user.is_also_following && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800 uppercase tracking-wider shrink-0">
                    Follows
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 mt-1">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 border border-teal-100 dark:border-teal-800 uppercase tracking-wider shrink-0 shadow-sm">
                {user.user_type}
              </span>
              
              {activeTab === 'collaborators' && user.collaboration_count && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800 uppercase tracking-wider shrink-0 shadow-sm">
                  {user.collaboration_count} collab{user.collaboration_count > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

        {/* Actions */}
        {user.id !== currentUser?.id && (
          <div className="flex items-center gap-2 opacity-90 group-hover:opacity-100 transition-opacity">
            {/* <Button
              size="sm"
              variant="outline"
              onClick={() => handleMessage(user)}
              className="!p-2.5 !rounded-xl border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400"
              title="Message"
            >
              <ChatBubbleLeftIcon className="h-5 w-5" />
            </Button> */}

            <Button
              size="sm"
              variant={followStatus ? "outline" : "primary"}
              onClick={() => handleFollowToggle(user.id)}
              loading={followingUsers.has(user.id)}
              className={`!px-4 !py-2 !rounded-xl font-semibold transition-all shadow-sm ${
                followStatus 
                  ? '!border-gray-200 dark:!border-gray-700 !text-gray-600 dark:!text-gray-400 hover:!bg-red-50 dark:hover:!bg-red-900/20 hover:!text-red-600 dark:hover:!text-red-400 hover:!border-red-200 dark:hover:!border-red-900/50' 
                  : '!bg-teal-600 hover:!bg-teal-700 !text-white shadow-teal-500/20'
              }`}
            >
              {followStatus ? (
                <span className="flex items-center gap-1.5">
                  <UserMinusIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Unfollow</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <UserPlusIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Follow</span>
                </span>
              )}
            </Button>
          </div>
        )}
      </motion.div>
    );
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'followers': return followers;
      case 'following': return following;
      case 'collaborators': return collaborators;
      default: return [];
    }
  };

  const getCurrentPagination = () => {
    return pagination[activeTab];
  };

  const getTabCount = (tab: 'followers' | 'following' | 'collaborators') => {
    return pagination[tab].total;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl h-[85vh] flex flex-col overflow-hidden border border-gray-100 dark:border-gray-800"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                {userName}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">Network & Relationships</p>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-8 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="flex space-x-8">
              {(['followers', 'following', 'collaborators'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  className={`relative py-4 text-sm font-semibold transition-colors ${
                    activeTab === tab
                      ? 'text-teal-600 dark:text-teal-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}>
                      {getTabCount(tab)}
                    </span>
                  </span>
                  {activeTab === tab && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600 dark:bg-teal-400 rounded-full"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="p-6 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800">
            <div className="relative group">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500 group-focus-within:text-teal-500 dark:group-focus-within:text-teal-400 transition-colors" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${activeTab}...`}
                className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500/20 dark:focus:ring-teal-400/20 focus:border-teal-500 dark:focus:border-teal-400 transition-all shadow-sm placeholder-gray-400 dark:placeholder-gray-500 text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
            {loading ? (
              <div className="p-6 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center space-x-4 p-4 rounded-xl border border-gray-100">
                    <div className="w-12 h-12 bg-gray-100 rounded-full"></div>
                    <div className="flex-1 space-y-3">
                      <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                      <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : getCurrentData().length > 0 ? (
              <div className="p-4 space-y-2">
                {getCurrentData().map(renderUserCard)}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center px-6">
                <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <UserIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  No {activeTab} found
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xs mx-auto">
                  {searchQuery
                    ? `We couldn't find any ${activeTab} matching "${searchQuery}"`
                    : `${userName} doesn't have any ${activeTab} yet.`
                  }
                </p>
              </div>
            )}
          </div>

          {/* Footer with pagination info */}
          {getCurrentData().length > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 text-center">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Showing {getCurrentData().length} of {getCurrentPagination().total} {activeTab}
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UserRelationshipModal;
